import { createContext, useContext, useState, useEffect, useCallback, useMemo } from "react";
import axios from "axios";
import { useAuth } from "@/lib/AuthContext";
import { FEATURE_FLAGS } from "@/config/featureFlags";
import logger from "@/lib/logger";

const SubscriptionContext = createContext(null);
const API = process.env.REACT_APP_BACKEND_URL;

const FREE_THEME_IDS = ["retro-cyber", "corporate-blue", "minimal-light", "minimalist-bw", "calm-focus"];

export function SubscriptionProvider({ children }) {
  const { user, isAuthenticated } = useAuth();
  const [subData, setSubData] = useState(null);
  const [drops, setDrops] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchStatus = useCallback(async () => {
    if (!isAuthenticated) {
      setSubData(null);
      setDrops([]);
      setLoading(false);
      return;
    }
    try {
      const [statusRes, dropsRes] = await Promise.all([
        axios.get(`${API}/api/subscription/status`, { withCredentials: true }),
        axios.get(`${API}/api/drops`, { withCredentials: true }).catch(() => ({ data: [] })),
      ]);
      setSubData(statusRes.data);
      setDrops(dropsRes.data || []);
    } catch (e) {
      setSubData(null);
      logger.error("fetchSubStatus:", e);
    }
    setLoading(false);
  }, [isAuthenticated]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  const tier = FEATURE_FLAGS.PAYWALL_ENABLED ? (subData?.tier || "free") : "pro";
  const isPro = FEATURE_FLAGS.PAYWALL_ENABLED ? (tier === "pro" || tier === "trial") : true;
  const isTrial = FEATURE_FLAGS.PAYWALL_ENABLED ? (tier === "trial") : false;
  const trial = useMemo(() => subData?.trial || { active: false, used: false, days_left: 0 }, [subData]);

  const isThemeUnlocked = useCallback((themeId) => {
    if (isPro) return true;
    if (FREE_THEME_IDS.includes(themeId)) return true;
    if (subData?.unlocked_themes?.includes(themeId)) return true;
    return false;
  }, [isPro, subData]);

  const isFeatureLocked = useCallback((feature) => {
    if (!FEATURE_FLAGS.PAYWALL_ENABLED) return false;
    if (isPro) return false;
    const locks = {
      adhd_mode: true,
      full_game_mode: true,
      advanced_analytics: true,
      distraction_lock: true,
      full_customization: true,
      unlimited_habits: true,
    };
    return locks[feature] ?? false;
  }, [isPro]);

  const createCheckout = useCallback(async (plan = "monthly") => {
    const origin = window.location.origin;
    const { data } = await axios.post(`${API}/api/subscription/checkout`, { origin_url: origin, plan }, { withCredentials: true });
    return data;
  }, []);

  const createThemeCheckout = useCallback(async (themePackId) => {
    const origin = window.location.origin;
    const { data } = await axios.post(`${API}/api/subscription/checkout-theme`, { origin_url: origin, theme_pack_id: themePackId }, { withCredentials: true });
    return data;
  }, []);

  const pollCheckoutStatus = useCallback(async (sessionId) => {
    const { data } = await axios.get(`${API}/api/subscription/checkout-status/${sessionId}`, { withCredentials: true });
    return data;
  }, []);

  const startTrial = useCallback(async () => {
    const { data } = await axios.post(`${API}/api/subscription/start-trial`, {}, { withCredentials: true });
    await fetchStatus();
    return data;
  }, [fetchStatus]);

  const claimDrop = useCallback(async (dropId) => {
    const { data } = await axios.post(`${API}/api/drops/${dropId}/claim`, {}, { withCredentials: true });
    await fetchStatus();
    return data;
  }, [fetchStatus]);

  const purchaseDrop = useCallback(async (dropId) => {
    const { data } = await axios.post(`${API}/api/drops/${dropId}/purchase`, {}, { withCredentials: true });
    return data;
  }, []);

  const refreshStatus = fetchStatus;

  // --- Split value into focused sub-memos (≤5 deps each) ---
  const subCore = useMemo(() => ({
    subData, loading, tier,
  }), [subData, loading, tier]);

  const subTier = useMemo(() => ({
    isPro, isTrial,
  }), [isPro, isTrial]);

  const subExtras = useMemo(() => ({
    trial, drops, freeThemes: FREE_THEME_IDS,
    themePacks: subData?.theme_packs || {},
    pricing: subData?.pricing || { monthly: 9, annual: 79, annual_monthly: 6.58, savings_pct: 27 },
  }), [trial, drops, subData]);

  const subChecks = useMemo(() => ({
    isThemeUnlocked, isFeatureLocked,
  }), [isThemeUnlocked, isFeatureLocked]);

  const subCheckout = useMemo(() => ({
    createCheckout, createThemeCheckout, pollCheckoutStatus,
  }), [createCheckout, createThemeCheckout, pollCheckoutStatus]);

  const subDropActions = useMemo(() => ({
    startTrial, claimDrop, purchaseDrop, refreshStatus,
  }), [startTrial, claimDrop, purchaseDrop, refreshStatus]);

  const subState = useMemo(() => ({
    ...subCore, ...subTier, ...subExtras,
  }), [subCore, subTier, subExtras]);

  const subOps = useMemo(() => ({
    ...subChecks, ...subCheckout, ...subDropActions,
  }), [subChecks, subCheckout, subDropActions]);

  const value = useMemo(() => ({
    ...subState, ...subOps,
  }), [subState, subOps]);

  return <SubscriptionContext.Provider value={value}>{children}</SubscriptionContext.Provider>;
}

export function useSubscription() {
  const ctx = useContext(SubscriptionContext);
  if (!ctx) throw new Error("useSubscription must be used within SubscriptionProvider");
  return ctx;
}
